/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useStandort } from '@/domain/mitarbeiter/features/anlageassistent/hooks/useStandort/useStandort';
import { type Tage } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/tage';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function useBezahlungSchema() {
  const { t } = useTranslation();
  const { currentStandort } = useStandortContext();
  const { standort } = useStandort(currentStandort.id);
  const BezahlungsartSchema = z
    .object({
      art: z
        .enum(['fixgehalt', 'stunden'])
        .optional()
        .refine((val) => !!val, {
          error: t(
            'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.art.validationError',
          ),
        }),
      tarifGruppe: z.string().optional(),
      stundenlohn: z.string().optional(),
      monatsgehalt: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.art === 'fixgehalt') {
        if (!data.stundenlohn) {
          ctx.addIssue({
            code: 'custom',
            path: ['stundenlohn'],
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.stundenlohn.validationError',
            ),
          });
        }
        if (
          standort?.tarifvertrag?.tarifvertragTyp === 'MANTEL_SYSGASTRO_BDS' &&
          !data.tarifGruppe
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['tarifGruppe'],
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.bezahlungsart.form.tarifgruppe.validationError',
            ),
          });
        }
      }
    });

  const SollstundenSchema = z
    .object({
      sollstunden: z
        .boolean()
        .optional()
        .refine((val) => val !== undefined, {
          error: t(
            'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.sollstunden.validationError',
          ),
        }),
      stundenpensum: z.enum(['monat', 'woche']).optional(),
      berechnungsartMonat: z.enum(['durchschnitt', 'exakt']).optional(),
      monatsstunden: z.string().optional(),
      unbezahlteUeberstundenMonat: z.string().optional(),
      wochenstunden: z.string().optional(),
      unbezahlteUeberstundenWoche: z.string().optional(),
      arbeitstage: z.custom<Tage[keyof Tage]>().array(),
      tagessoll: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.sollstunden) {
        if (!data.stundenpensum) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.stundenpensum.validationError',
            ),
            path: ['stundenpensum'],
          });
        }
        if (data.stundenpensum === 'monat') {
          if (!data.berechnungsartMonat) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.berechnungsartMonat.validationError',
              ),
              path: ['berechnungsartMonat'],
            });
          }
          if (data.berechnungsartMonat === 'durchschnitt') {
            if (!data.monatsstunden) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.monatsstunden.validationError',
                ),
                path: ['monatsstunden'],
              });
            }
          } else if (data.berechnungsartMonat === 'exakt') {
            if (data.arbeitstage.length === 0) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.arbeitstage.validationError',
                ),
                path: ['arbeitstage'],
              });
            }
            if (!data.tagessoll) {
              ctx.addIssue({
                code: 'custom',
                message: t(
                  'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.tagessoll.validationError',
                ),
                path: ['tagessoll'],
              });
            }
          }
        } else if (data.stundenpensum === 'woche') {
          if (!data.wochenstunden) {
            ctx.addIssue({
              code: 'custom',
              message: t(
                'domain.mitarbeiter.create.steps.bezahlung.sollstunden.form.wochenstunden.validationError',
              ),
              path: ['wochenstunden'],
            });
          }
        }
      }
    });

  const ZuschlaegeSchema = z
    .object({
      zuschlaege: z
        .boolean()
        .optional()
        .refine((val) => val !== undefined, {
          error: t(
            'domain.mitarbeiter.create.steps.bezahlung.zuschlaege.form.zuschlaege.validationError',
          ),
        }),
      zuschlagsgruppe: z.string().optional(),
      pauschaleZuschlaege: z.string().optional(),
      uebertariflicheZahlung: z.string().optional(),
      /**
       * Dieses Feld wurde nur hinzugefügt um die TypeInferenz ohne strict mode zu gewährleisten, da die restlichen Felder alle optional sind. Es sollte nicht in der UI verwendet werden.
       * TODO: Entfernen sobald strict mode aktiv ist
       * */
      strict: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.zuschlaege) {
        if (
          !(
            data.zuschlagsgruppe ||
            data.pauschaleZuschlaege ||
            data.uebertariflicheZahlung
          )
        ) {
          ctx.addIssue({
            code: 'custom',
            path: [],
          });
        }
      }
    });
  return {
    BezahlungsartSchema,
    SollstundenSchema,
    ZuschlaegeSchema,
  };
}
